/* eslint-disable @typescript-eslint/no-explicit-any */
import { Grid, LinearProgress, Paper, Typography } from '@material-ui/core'
import moment from 'moment-timezone'
import { FC, useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useHistory, useRouteMatch } from 'react-router'
import { Dispatch } from 'redux'
import { toast } from 'react-toastify'
import { reset, SubmissionError } from 'redux-form'
import { createApplication, getApartmentLead } from '../../actions/applications'
import ChangeLanguageForm from '../../components/ChangeLanguageForm'
import Container from '../../components/Container'
import {
  selectCreateApplication,
  selectLeadApplication
} from '../../selectors/applications'
import i18n from '../../translations/i18n'
import { Apartment } from '../../types/apartments'
import { Application, PartnerLead } from '../../types/applications'
import { StateInterface } from '../../types/states'
import ApartmentsService from '../../services/apartments'
import { validateEmail as validateEmailService } from '../../services/users'
import { useSessionTimeout } from '../../utils/useSessionTimeout'
// import { bindSubmitActionToPromise } from '../../utils/forms'
import NewApplicationForm, { isOneAdult } from './NewApplicationForm'
import MinimalNewApplicationForm from './MinimalNewApplicationForm'
import NewApplicationProgress from './Progress'

const App: FC = (props: any) => {
  const history = useHistory()

  const routeMatch = useRouteMatch()
  const apartmentCode = (routeMatch.params as any).apartmentCode
  const step = Number.parseInt((routeMatch.params as any).step || 0)
  const {
    location,
    getLeadData,
    application,
    lead,
    createApplication: submitApplication,
    resetNewApplicationForm
  } = props
  const currentLanguage = i18n.language
  const initialValue = {
    firstName: '',
    lastName: '',
    email: '',
    phone: { phone: '358', countryDialCode: '358' },
    availabilities: [{ startDate: moment() }],
    notes: '',
    contactInformationConsent: false,
    agreed: false
    // superLike: false // superLike temporarily disabled
  }
  const [value, setValue] = useState(initialValue)
  const [applicationId, setApplicationId] = useState('')
  const [apartment, setApartment] = useState<Apartment | null>(null)
  const [isApartmentLoading, setIsApartmentLoading] = useState(true)
  const [intermediateLoading, setIntermediateLoading] = useState(false)

  const isMinimalAnnouncement =
    (apartment as any)?.announcementType === 'minimal'

  const navigateTo = useCallback(
    (newStep) => {
      history.push(`/new-application/${apartmentCode}/steps/${newStep}`)
    },
    [history, apartmentCode]
  )

  const handleSessionTimeout = useCallback(() => {
    if (application) return
    resetNewApplicationForm()
    navigateTo(0)
    toast.info(i18n.t('timeoutMessage'), { autoClose: false })
  }, [application, navigateTo, resetNewApplicationForm])

  useSessionTimeout(60 * 60 * 1000, handleSessionTimeout)

  useEffect(() => {
    let mounted = true
    const fetchApartment = async () => {
      try {
        setIsApartmentLoading(true)
        const fetchedApartment = await ApartmentsService.getApartmentByCode(
          apartmentCode
        )

        if (mounted) {
          setApartment(fetchedApartment)
        }
      } catch (error) {
        if (mounted) {
          setApartment(null)
        }
      } finally {
        if (mounted) {
          setIsApartmentLoading(false)
        }
      }
    }
    fetchApartment()
    const qs = new URLSearchParams(location.search)
    if (qs && qs.get('source')) {
      getLeadData(qs.get('source'))
    }
    return () => {
      mounted = false
    }
  }, [apartmentCode, location.search, getLeadData])

  useEffect(() => {
    if (isMinimalAnnouncement && step !== 0) {
      navigateTo(0)
    }
  }, [isMinimalAnnouncement, step, navigateTo])

  useEffect(() => {
    if (lead && lead.leadData && lead.leadData.metadata) {
      const leadData = lead.leadData.metadata
      const updatedValue = {
        ...initialValue,
        firstName: leadData.name.split(' ')[0] || '',
        lastName: leadData.name.split(' ')[1] || '',
        email: leadData.email || '',
        notes: leadData.notes || '',
        agreed:
          leadData.agreed === true || leadData.agreed === 'true' || false,
        contactInformationConsent:
          leadData.contactInformationConsent === true ||
          leadData.contactInformationConsent === 'true' ||
          false
      }
      if (leadData.phone && leadData.phone.trim()) {
        updatedValue.phone = { countryDialCode: '358', phone: leadData.phone }
      }
      setValue(updatedValue)
      setApplicationId(lead.leadData.applicationId)
    }
  }, [lead])

  const handleMinimalSubmit = useCallback(
    (formValues: any) =>
      new Promise((promiseResolve, promiseReject) => {
        setIntermediateLoading(true)
        const stopLoading = () => setIntermediateLoading(false)
        const payload = {
          ...formValues,
          notes: formValues.notes || 'Olen kiinnostunut kohteestanne. Ottaisitko yhteyttä?',
          name: `${formValues.firstName || ''} ${formValues.lastName || ''}`.trim(),
          apartmentCode,
          language: currentLanguage,
          applicationId,
          contactInformationConsent:
            formValues.contactInformationConsent || false
        }

        // SuperLike temporarily disabled:
        // if (typeof window !== 'undefined') {
        //   if (formValues?.superLike) {
        //     window.localStorage.setItem(
        //       'applicationPaymentProduct',
        //       JSON.stringify({ productTags: ['superLike'] })
        //     )
        //   } else {
        //     window.localStorage.removeItem('applicationPaymentProduct')
        //   }
        // }

        if (typeof window !== 'undefined' && (window as any).gtag) {
          ;(window as any).gtag('event', 'conversion', {
            send_to: 'AW-803885890/lpiGCLLGj90bEMKmqf8C'
          })
        }

        const submitPayload = () =>
          submitApplication({
            formData: payload,
            resolve: (result: {
              application: Application
              shouldOpenPaymentPopup?: boolean
            }) => {
              if (result?.shouldOpenPaymentPopup && result.application) {
                history.push(`/application/${result.application.code}/payment`)
              }

              stopLoading()
              promiseResolve(result)
            },
            reject: (error: unknown) => {
              stopLoading()
              promiseReject(error)
            }
          })

        if (payload.email) {
          validateEmailService(payload.email)
            .then((isValid) => {
              if (!isValid) {
                stopLoading()
                promiseReject(
                  new SubmissionError({
                    email: i18n.t('validation.emailValidationError')
                  })
                )
                return
              }
              submitPayload()
            })
            .catch(() => {
              stopLoading()
              promiseReject(
                new SubmissionError({
                  email: i18n.t('validation.emailValidationError')
                })
              )
            })
        } else {
          submitPayload()
        }
      }),
    [
      applicationId,
      submitApplication,
      apartmentCode,
      currentLanguage,
      history
    ]
  )

  if (isApartmentLoading) {
    return <LinearProgress />
  }

  if (!apartment) {
    return <LinearProgress />
  }

  return (
    <>
      {apartment.address ? (
        <>
          {
            apartment.status === 'rented' ? (
              <Grid container justify="center">
                <Grid item xs={10} sm={8} md={6} lg={4}>
                  <Paper style={{ margin: '50px 0', padding: '30px' }}>
                    <Typography variant='h5' gutterBottom style={{ textAlign: 'center' }}>
                      {i18n.t('newApplication.notAvailable')}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            ) : (
              <>
                <Container
                  styles={{ maxWidth: '1131px', margin: 'auto', padding: '8px 20px' }}
                >
                  <NewApplicationProgress
                    progress={
                      isMinimalAnnouncement
                        ? 100
                        : step === 0
                          ? 33
                          : ((step + 1) / 4) * 100
                    }
                  />
                </Container>

                <ChangeLanguageForm
                  go={(_: string) => {
                    navigateTo(step)
                  }}
                />
                <Grid container justify='center'>
                  {!application &&
                    (isMinimalAnnouncement ? (
                      <MinimalNewApplicationForm
                        onSubmit={handleMinimalSubmit}
                        initialValues={{ ...value }}
                        intermediateLoading={intermediateLoading}
                      />
                    ) : (
                      <NewApplicationForm
                        onSubmit={async (_values: any) => {
                          const isIntermediateStep =
                            step === 0 ||
                            step === 1 ||
                            (step === 2 && !isOneAdult(_values))

                          if (isIntermediateStep) {
                            setIntermediateLoading(true)
                          }

                          try {
                            if (step === 0 && _values?.email) {
                              const emailIsValid = await validateEmailService(_values.email)
                              if (!emailIsValid) {
                                throw new SubmissionError({
                                  email: i18n.t('validation.emailValidationError')
                                })
                              }
                            }

                            if (isIntermediateStep) {
                              navigateTo(step + 1)
                              return
                            }

                            if (typeof window !== 'undefined' && (window as any).gtag) {
                              ;(window as any).gtag('event', 'conversion', {
                                send_to: 'AW-803885890/lpiGCLLGj90bEMKmqf8C'
                              })
                            }
                            submitApplication({
                              ..._values,
                              apartmentCode,
                              language: currentLanguage,
                              applicationId: applicationId
                            })
                          } finally {
                            if (isIntermediateStep) {
                              setIntermediateLoading(false)
                            }
                          }
                        }}
                        back={() => {
                          navigateTo(step - 1)
                        }}
                        step={step}
                        intermediateLoading={intermediateLoading}
                        initialValues={{ ...value }}
                      />
                    ))}
                </Grid>
              </>
          )}
        </>
      ) : (
        <LinearProgress />
      )}
    </>
  )
}

interface PropsFromState {
  loading: boolean
  application: Application | null
  error: string | null
  lead: PartnerLead | null
}

const mapStateToProps = (state: StateInterface): PropsFromState => {
  const createApplication = selectCreateApplication(state)
  const getLeadApplication = selectLeadApplication(state)
  return {
    ...createApplication,
    ...getLeadApplication
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  createApplication: (data: any) => dispatch(createApplication(data)),
  getLeadData: (leadId: string) => dispatch(getApartmentLead(leadId)),
  resetNewApplicationForm: () => dispatch(reset('newApplication'))
})

export default connect(mapStateToProps, mapDispatchToProps)(App)
